import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useApi } from "@/hooks/use-api";
import { Course } from "@/types/course";
import { Copy, QrCode } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface ShareCourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  course: Course;
}

export const ShareCourseDialog = ({
  isOpen,
  onClose,
  course,
}: ShareCourseDialogProps) => {
  const { fetchCourseShareInfo, fetchRawMedia } = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [isCopyingQr, setIsCopyingQr] = useState(false);

  useEffect(() => {
    const loadShareInfo = async () => {
      if (isOpen && !isLoading && (!course.qrCodeUrl || !course.shortUrl)) {
        setIsLoading(true);
        try {
          await fetchCourseShareInfo(course.id);
        } catch (error) {
          console.error("Failed to fetch share info", error);
          toast.error("Failed to load sharing information");
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadShareInfo();
  }, [
    isOpen,
    course.id,
    course.qrCodeUrl,
    course.shortUrl,
    fetchCourseShareInfo,
    isLoading,
  ]);

  const handleCopy = () => {
    if (course.shortUrl) {
      navigator.clipboard.writeText(course.shortUrl);
      toast.success("Link copied to clipboard");
    }
  };

  const handleCopyQrImage = async () => {
    if (!course.qrCodeUrl || isLoading || isCopyingQr) {
      return;
    }

    if (
      !navigator.clipboard?.write ||
      typeof window.ClipboardItem === "undefined"
    ) {
      toast.error("Unable to copy QR image");
      return;
    }

    setIsCopyingQr(true);
    try {
      const blob = await fetchRawMedia(course.qrCodeUrl);
      const mimeType = blob.type || "image/png";
      await navigator.clipboard.write([
        new ClipboardItem({ [mimeType]: blob }),
      ]);
      toast.success("QR code image copied to clipboard");
    } catch (error) {
      console.error("Failed to copy QR image", error);
      toast.error("Unable to copy QR image");
    } finally {
      setIsCopyingQr(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share course</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center space-y-6 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center w-48 h-48">
              <Spinner />
              <p className="text-xs text-muted-foreground mt-4">
                Generating sharing info...
              </p>
            </div>
          ) : (
            <>
              {course.qrCodeUrl && (
                <div className="flex flex-col items-center space-y-2">
                  <div className="bg-white p-2 rounded-lg border shadow-sm">
                    <img
                      src={course.qrCodeUrl}
                      alt={`QR Code for ${course.title}`}
                      className="w-48 h-48 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyQrImage}
                    disabled={isLoading || isCopyingQr}
                    className="text-sm mt-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                  >
                    Click to copy QR code as image
                  </button>
                </div>
              )}

              {!course.qrCodeUrl && (
                <div className="flex flex-col items-center justify-center w-48 h-48 bg-muted rounded-lg">
                  <QrCode className="w-12 h-12 text-muted-foreground opacity-50" />
                  <p className="text-xs text-muted-foreground mt-2">
                    No QR code available
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex w-full items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Input
                id="link"
                value={
                  isLoading
                    ? "Loading..."
                    : course.shortUrl || "No link available"
                }
                readOnly
                className="h-9"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="px-3"
              onClick={handleCopy}
              disabled={isLoading || !course.shortUrl}
            >
              <span className="sr-only">Copy</span>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
